import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import {
    ModalityHero, ProblemSection, HowItHelpsSection,
    ThePracticeSection, BeginTogetherSection,
} from "./modalityShared";

const steps = [
    { n: "I", title: "Creating Safety First", body: "Before any processing begins, we establish a sense of emotional safety and attunement. Brainspotting works deeply — and that depth requires a foundation of trust and groundedness in the therapeutic relationship." },
    { n: "II", title: "Following Your Nervous System", body: "Brainspotting sessions are led by your body's own processing — not a predetermined agenda. We work at the pace your nervous system can hold, following what arises with curiosity and care rather than force." },
    { n: "III", title: "Integration Over Time", body: "As the nervous system releases what it has been holding, many clients notice lasting shifts in how they feel, respond, and relate to themselves. The work integrates gradually — often continuing to unfold between sessions." },
];

const phrases = [
    "Your nervous system has been carrying this for years.",
    "Brainspotting reaches where words cannot.",
    "Healing happens when the nervous system finally feels safe enough to let go.",
    "Calm is not the absence of feeling. It is the presence of safety.",
];

export default function BrainspottingPage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="Brainspotting Therapy in Wisconsin & Colorado | Alchemy Practice"
                description="Brainspotting therapy for emotional overwhelm, anxiety, and feeling stuck. Virtual sessions serving Wisconsin and Colorado. Alchemy Practice with Austine LPC."
            />
            <Nav />
            <RevealOnScroll>
                <ModalityHero
                    testidPrefix="brainspotting"
                    eyebrow="Brainspotting Therapy"
                    headline="Brainspotting Therapy for Anxiety, Emotional Overwhelm, and"
                    italic="Feeling Stuck"
                    subhead="Your mind understands. Your nervous system still reacts like the danger is happening now."
                />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <ProblemSection
                    testidPrefix="brainspotting"
                    eyebrow="What Brings You Here"
                    headline="When Insight Is Not Changing"
                    italic="the Feeling"
                    firstLine="You know your reactions do not fully make sense logically."
                    paragraphs={[
                        "You understand your patterns intellectually. Yet emotionally, your body still tightens with anxiety, fear, shame, overwhelm, or self-doubt.",
                        "You may feel emotionally exhausted from constantly trying to manage your internal world through thinking alone.",
                        "For deeply feeling people, this disconnect can become incredibly frustrating. Part of you understands. Another part still feels trapped in the same emotional reactions.",
                    ]}
                />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <HowItHelpsSection
                    testidPrefix="brainspotting"
                    eyebrow="The Approach"
                    headline="How Brainspotting"
                    italic="Helps"
                    paragraphs={[
                        "Brainspotting helps access emotional experiences that are often difficult to fully reach through talking alone.",
                        "Rather than working only with thoughts and insight, Brainspotting works with the deeper emotional and physiological layers of experience stored within the nervous system.",
                        "Clients often describe this work as emotionally deep, grounding, clarifying, and surprisingly relieving.",
                        "Over time, many clients notice they feel calmer, less reactive, more emotionally regulated, and more connected to themselves.",
                    ]}
                    quote={'"What I appreciate most about Brainspotting is that it allows people to process experiences their nervous system has been carrying for years, even when they struggle to fully put those experiences into words."'}
                />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ThePracticeSection
                    testidPrefix="brainspotting"
                    eyebrow="The Practice"
                    headline="Thoughtful, Personalized"
                    italic="Care"
                    paragraphs={[
                        "At Alchemy Practice, Brainspotting is integrated thoughtfully within a broader therapy process. Sessions are paced intentionally and tailored to your nervous system, emotional history, and goals.",
                        "Healing does not happen through force. It happens through creating enough safety for the nervous system to finally let go of what it has been holding.",
                    ]}
                />
                <SectionDivider color="gold" bg="#F2EDE6" />
                <BeginTogetherSection steps={steps} testidPrefix="brainspotting" />
                <Marquee phrases={phrases} />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
